import { useTheme } from "@emotion/react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Radio,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TextField as MuiTextField,
  Typography,
  Stack,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../theme";
import FormService from "../../services/doctorServices/FormService";
import Headeer from "../../components/Headeer";
import PatientService from "../../services/doctorServices/patientService";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import WarningSnackbar from "../../components/SnackbarComponent";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import TableChartIcon from "@mui/icons-material/TableChart";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileService from "../../services/doctorServices/FileService";
import MailIcon from "@mui/icons-material/Mail";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import FunctionsIcon from "@mui/icons-material/Functions";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import CartilageMap from "../../components/CartilageMap";
import RatingScaleSlider from "../../components/RatingScaleSlider";

export default function FormInfo() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('forms');
  const formService = new FormService();
  const patientService = new PatientService();
  const fileService = new FileService();
  const { formId } = useParams();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formInfo, setFormInfo] = useState({});
  const [patients, setPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [formTokens, setFormTokens] = useState([]);
  const [formInvitations, setFormInvitations] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [openDeleteForm, setOpenDeleteForm] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);

  // Copy Form Dialog States
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [copyFormName, setCopyFormName] = useState("");
  const [selectedFileId, setSelectedFileId] = useState("");
  const [allFiles, setAllFiles] = useState([]);
  useEffect(() => {
    // UUID format kontrolü: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if ((id && !uuidRegex.test(id)) || (formId && !uuidRegex.test(formId))) {
      // Geçersiz UUID formatı, direkt 404'e yönlendir
      navigate("/404", { replace: true });
      return;
    }

    const fetchFormInfo = async () => {
      try {
        const response = await formService.getFormInfo(formId);
        // Response boş veya null ise 404'e yönlendir
        if (!response || !response.id) {
          navigate("/404", { replace: true });
          return;
        }
        console.log("Form Bilgileri Başarıyla Getirildi: ", response);
        setFormInfo(response || {});
      } catch (error) {
        console.log("Form bilgileri alınırken bir sorun oluştu: ", error);
        // 404 veya geçersiz ID durumunda 404 sayfasına yönlendir
        if (error.response?.status === 404 || !error.response) {
          navigate("/404", { replace: true });
        } else {
          // Diğer hatalar için de 404'e yönlendir (form bulunamadı)
          navigate("/404", { replace: true });
        }
      }
    };

    const fetchAllPatient = async () => {
      try {
        const response = await patientService.getAllPatients();
        const filteredPatients = response?.patients?.filter((patient) =>
          patient.fileIds.includes(id)
        );
        setPatients(filteredPatients || []);
        console.log();
      } catch (error) {
        console.log("Hastalar getirilirken bir sorun oluştu: ", error);
        setPatients([]);
      }
    };

    const fetchAllFiles = async () => {
      try {
        const response = await fileService.getAllFiles();
        setAllFiles(response || []);
      } catch (error) {
        console.log("Dosyalar getirilirken bir sorun oluştu: ", error);
        setAllFiles([]);
      }
    };

    fetchFormInfo();
    fetchAllPatient();
    fetchAllFiles();
  }, [id, formId, navigate]);

  useEffect(() => {
    if (formInfo?.type === "for patients") {
      const fetchFormInvitations = async () => {
        try {
          const invitations = await formService.getFormInvitations(formId);
          setFormInvitations(invitations || []);
          console.log("getFormInvitations:", invitations);
        } catch (error) {
          console.log("Form davetleri getirilirken bir sorun oluştu:", error);
          setFormInvitations([]);
        }
      };
      fetchFormInvitations();
    }
  }, [formInfo, formId]);

  const createdAt = formInfo?.createdAt?.split("T")[0] ?? "";
  const updatedAt = formInfo?.updatedAt?.split("T")[0] ?? "";
  const totalQuestions = Array.isArray(formInfo?.questions) ? formInfo.questions.length : 0;
  const scoringFormulaName = formInfo?.ScoringFormula?.name || "—";
  
  // Format repeat schedule for display
  const formatRepeatSchedule = (schedule) => {
    if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
      return "—";
    }
    return schedule.join(", ");
  };

  const formMetaDetails = useMemo(() => [
    {
      label: t('formName'),
      value: formInfo?.name || "—",
      icon: <AssignmentOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
    },
    {
      label: t('fileName'),
      value: formInfo?.File?.name || "—",
      icon: <FolderOpenOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
    },
    {
      label: t('createdAt'),
      value: createdAt || "—",
      icon: <CalendarMonthOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[400]} 100%)`,
    },
    {
      label: t('formType'),
      value: formInfo?.type === "for me" ? t('forDoctor') : t('forPatient'),
      icon: <CategoryOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[400]} 100%)`,
    },
    {
      label: t('totalQuestions'),
      value: totalQuestions,
      icon: <DescriptionOutlinedIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
    },
    {
      label: t('scoringFormula'),
      value: scoringFormulaName,
      icon: <FunctionsIcon fontSize="medium" />,
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
    },
    // Only show repeat_count and repeat_schedule for "for patients" type
    ...(formInfo?.type === "for patients" ? [
      {
        label: t('totalRepetitions'),
        value: formInfo?.repeat_count || "—",
        icon: <ScheduleIcon fontSize="medium" />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
      },
      {
        label: t('repeatSchedule'),
        value: formatRepeatSchedule(formInfo?.repeat_schedule),
        icon: <ScheduleIcon fontSize="medium" />,
        gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
      },
    ] : []),
  ], [formInfo, createdAt, totalQuestions, scoringFormulaName, colors, t]);

  const dataGridCardStyles = {
    background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
    borderRadius: "16px",
    border: `1px solid ${colors.grey[800]}30`,
    boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
    overflow: "hidden",
    width: "100%",
  };

  const dataGridWrapperStyles = {
    px: 3,
    pb: 3,
    minHeight: 360,
    '& .MuiDataGrid-root': {
      border: 'none',
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: `${colors.blueAccent[700]}60`,
      borderBottom: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: 0,
      fontWeight: 700,
      fontSize: '0.95rem',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
    },
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${colors.grey[800]}20`,
      fontSize: '0.9rem',
    },
    '& .MuiDataGrid-row': {
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: `${colors.blueAccent[800]}20`,
        cursor: 'pointer',
      },
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: `${colors.primary[500]}20`,
    },
    '& .MuiDataGrid-virtualScroller': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: `${colors.blueAccent[700]}40`,
      borderTop: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: 0,
    },
    '& .MuiTablePagination-root': {
      color: colors.grey[100],
    },
    '& .MuiCheckbox-root': {
      color: `${colors.greenAccent[200]} !important`,
    },
    '& .MuiDataGrid-toolbarContainer': {
      padding: 0,
    },
  };

  const questionMeta = useMemo(() => ({
    text: {
      title: t('freeTextQuestion'),
      subtitle: t('patientsProvideOpenNarrative'),
      gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[400]} 100%)`,
      icon: <TextFieldsOutlinedIcon />,
      chipLabel: "Text",
    },
    multiple_choice: {
      title: t('multipleChoiceQuestion'),
      subtitle: t('optionsWithImportance'),
      gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <FormatListBulletedOutlinedIcon />,
      chipLabel: "Multiple Choice",
    },
    table: {
      title: t('tableQuestion'),
      subtitle: t('evaluateAnswersAcrossMatrix'),
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <TableChartIcon />,
      chipLabel: "Table",
    },
    image: {
      title: t('imageQuestion'),
      subtitle: t('selectAnatomicalRegions'),
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <ImageIcon />,
      chipLabel: "Image",
    },
    rating_scale: {
      title: t('ratingScaleQuestion'),
      subtitle: t('ratingScaleSubtitle'),
      gradient: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <LinearScaleIcon />,
      chipLabel: "VAS",
    },
    default: {
      title: t('question'),
      subtitle: "",
      gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
      icon: <DescriptionOutlinedIcon />,
      chipLabel: t('question'),
    },
  }), [colors, t]);

  const CustomToolbar = ({ placeholder }) => (
    <GridToolbarContainer
      sx={{
        p: 2,
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        background: `linear-gradient(135deg, ${colors.blueAccent[800]}20 0%, ${colors.primary[500]} 100%)`,
        borderBottom: `2px solid ${colors.blueAccent[500]}30`,
        borderRadius: '12px 12px 0 0',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <GridToolbarColumnsButton
          sx={{
            color: colors.grey[100],
            backgroundColor: `${colors.blueAccent[700]}40`,
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${colors.blueAccent[600]}60`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
            },
            transition: 'all 0.3s ease',
          }}
        />
        <GridToolbarDensitySelector
          sx={{
            color: colors.grey[100],
            backgroundColor: `${colors.blueAccent[700]}40`,
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${colors.blueAccent[600]}60`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
            },
            transition: 'all 0.3s ease',
          }}
        />
        <GridToolbarExport
          sx={{
            color: 'white',
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
            border: `1px solid ${colors.greenAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.greenAccent[500]}60`,
            },
            transition: 'all 0.3s ease',
          }}
        />
      </Box>
    </GridToolbarContainer>
  );

  const columns = useMemo(() => [
    {
      field: "firstName",
      headerName: t('name'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "lastName",
      headerName: t('lastName'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "email",
      headerName: t('email'),
      flex: 1,
    },
    {
      field: "dateOfBirth",
      headerName: t('dateOfBirth'),
      flex: 1,
    },
    {
      field: "gender",
      headerName: t('gender'),
      flex: 1,
    },
    {
      field: "doctorName",
      headerName: t('doctor'),
      flex: 1,
      valueGetter: (params) => {
        const doctor = params.row.Doctor;
        return doctor ? `${doctor.name} ${doctor.surname}` : "—";
      },
    },
    ...(formInfo?.type === "for patients" ? [{
      field: "formInvitations",
      headerName: t('formInvitations'),
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const patientId = params.row.id;
        const patientInvitations = formInvitations.filter(inv => inv.patient_id === patientId);
        
        if (patientInvitations.length === 0) {
          return (
            <Typography sx={{ color: colors.grey[500], fontStyle: "italic" }}>
              {t('noInvitationsSent')}
            </Typography>
          );
        }

        // Sıralama: en eski solda, en yeni sağda (1-2-3 sıralaması)
        const sortedInvitations = [...patientInvitations].sort((a, b) => {
          // count değerine göre sırala (1, 2, 3...)
          return (a.count || 1) - (b.count || 1);
        });
        
        // Son 5 invitation'ı göster (eğer 6 tane varsa 2-3-4-5-6 göster)
        const lastFiveInvitations = sortedInvitations.slice(-5);

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            {lastFiveInvitations.map((invitation) => {
              // expires_at kontrolü
              const isExpired = invitation.expires_at && new Date(invitation.expires_at) < new Date();
              
              return (
                <Box
                  key={invitation.id}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {invitation.status === "pending" ? (
                    <MailIcon 
                      sx={{ 
                        fontSize: 32, 
                        color: isExpired ? colors.redAccent[500] : "#FFC107",
                        filter: isExpired 
                          ? "drop-shadow(0 2px 4px rgba(219, 79, 74, 0.4))"
                          : "drop-shadow(0 2px 4px rgba(255, 193, 7, 0.4))"
                      }} 
                    />
                  ) : (
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 32, 
                        color: colors.greenAccent[500],
                        filter: "drop-shadow(0 2px 4px rgba(16, 185, 129, 0.4))"
                      }} 
                    />
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      minWidth: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: invitation.status === "pending" 
                        ? (isExpired ? colors.redAccent[500] : "#FFC107")
                        : colors.greenAccent[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `3px solid ${colors.primary[600]}`,
                      boxShadow: `0 3px 12px rgba(0, 0, 0, 0.4)`,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "0.75rem",
                        lineHeight: 1,
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      {invitation.count || 1}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      },
    }] : []),
  ], [formInfo, formInvitations, colors, t]);

  const handleFill = () => {
    if (selectedPatients.length === 0) {
      setAlertMessage(t('pleaseSelectAtLeast1Patient'));
      setAlertSeverity("warning");
      setShowAlert(true);
      return;
    }

    if (selectedPatients.length > 1) {
      setAlertMessage(t('youCanOnlySelect1Patient'));
      setAlertSeverity("warning");
      setShowAlert(true);
      return;
    }

    navigate(`/files/${id}/${formId}/fill-out`, {
      state: { selectedPatients },
    });
  };

  const handleSend = async () => {
  if (selectedPatients.length === 0) {
    setAlertMessage(t('pleaseSelectAtLeast1PatientLower'));
    setAlertSeverity("warning");
    setShowAlert(true);
    return;
  }

  // ✅ Maksimum hasta sayısı kontrolü (performans için)
  if (selectedPatients.length > 50) {
    setAlertMessage(t('youCanSelectMaximum50Patients'));
    setAlertSeverity("warning");
    setShowAlert(true);
    return;
  }

  setSendEmailLoading(true);
  try {
    // ✅ Tüm seçili hastaların ID'lerini gönder
    const data = {
      patientIds: selectedPatients.map(p => p.id),
      expiresInDays: 7,
    };

    const response = await formService.sendFormEmail(data, formId);

    console.log("Invite tablosuna davetiyeler eklendi:", response);
    
    // ✅ Dinamik success mesajı
    const patientCount = selectedPatients.length;
    const plural = patientCount > 1 ? 's' : '';
    setAlertMessage(
      t('formSentSuccessfully', { count: patientCount, plural })
    );
    setAlertSeverity("success");
    setShowAlert(true);
    
    // ✅ Seçimi temizle
    setSelectedPatients([]);
    
    // ✅ Form davetlerini yeniden yükle
    if (formInfo?.type === "for patients") {
      try {
        const invitations = await formService.getFormInvitations(formId);
        setFormInvitations(invitations || []);
      } catch (error) {
        console.log("Form davetleri güncellenirken bir sorun oluştu:", error);
      }
    }
  } catch (error) {
    console.error("Invite tablosu oluşturulurken bir sorun oluştu:", error);
    setAlertMessage(t('errorSendingEmails'));
    setAlertSeverity("error");
    setShowAlert(true);
  } finally {
    setSendEmailLoading(false);
  }
};




  const handleEditForm = () => {
    navigate(`/files/${id}/${formId}/edit`);
  };
  const handleCloseSnackbar = () => {
    setShowAlert(false);
  };

  const handleDeleteForm = async () => {
    try {
      const response = await formService.deleteForm(formId);
      console.log("Form Başarıyla Silindi", response);
      
      // Success message
      setAlertMessage(t('formDeletedSuccessfully'));
      setAlertSeverity("success");
      setShowAlert(true);
      setOpenDeleteForm(false);
      
      // Redirect after 1.5 seconds
      setTimeout(() => {
        navigate(`/files/${id}`);
      }, 1500);
    } catch (error) {
      console.error("Form silinirken hata:", error);
      setOpenDeleteForm(false);
      // 403: PermissionAlertProvider (axios interceptor + permissionAlertEmitter)
      if (error.response?.status === 403) {
        return;
      }
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("errorDeletingForm");
      setAlertMessage(errorMessage);
      setAlertSeverity("error");
      setShowAlert(true);
    }
  };
  const closeDeleteFormDialog = () => {
    setOpenDeleteForm(false);
  };

  // Copy Form Functions
  const handleOpenCopyDialog = () => {
    setCopyFormName(formInfo?.name ? `${formInfo.name} - Copy` : t('formCopy'));
    setSelectedFileId("");
    setOpenCopyDialog(true);
  };

  const handleCloseCopyDialog = () => {
    setOpenCopyDialog(false);
    setCopyFormName("");
    setSelectedFileId("");
  };

  const handleCopyForm = () => {
    if (!selectedFileId) {
      setAlertMessage(t('pleaseSelectAFile'));
      setShowAlert(true);
      return;
    }

    const trimmedName = copyFormName.trim();
    if (!trimmedName) {
      setAlertMessage(t('pleaseEnterFormName'));
      setShowAlert(true);
      return;
    }

    const targetFileId = selectedFileId;
    const formType = formInfo?.type || "for patients";
    const typeQuery = encodeURIComponent(formType);

    handleCloseCopyDialog();

    navigate(`/Create-NewForm/${targetFileId}?type=${typeQuery}`, {
      state: {
        copyFormDraft: {
          name: trimmedName,
          description: formInfo?.description || "",
          type: formType,
          questions: formInfo?.questions || [],
          scoring_formula_id: formInfo?.scoring_formula_id || null,
          repeat_count: formInfo?.repeat_count,
          repeat_schedule: formInfo?.repeat_schedule || [],
        },
      },
    });
  };

  return (
    <Box sx={{ marginLeft: 3, marginRight: 2 }}>
      <Box>
        <Headeer
          title={t('formDetail')}
          subtitle={t('displayFormDetailPage')}
        />
      </Box>
      <Box
        sx={{
          width: "95%",
          mx: "auto",
          mb: 4,
        }}
      >
        <Box
          sx={{
            position: "relative",
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 60%, ${colors.blueAccent[900]}80 100%)`,
            border: `1px solid ${colors.blueAccent[700]}40`,
            boxShadow: `0 16px 40px ${colors.primary[900]}50`,
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at top right, ${colors.blueAccent[700]}30, transparent 55%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: colors.grey[100], letterSpacing: 0.3 }}
              >
                {t('formOverview')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], opacity: 0.85 }}
              >
                {t('formOverviewDescription')}
              </Typography>
            </Box>
            <Chip
              label={`${t('updated')}: ${updatedAt || "—"}`}
              sx={{
                backgroundColor: `${colors.blueAccent[700]}40`,
                color: colors.blueAccent[100],
                fontWeight: 600,
                borderRadius: 2,
                px: 1.5,
              }}
            />
          </Box>
          <Grid container spacing={3} sx={{ position: "relative" }}>
            {formMetaDetails.map((detail) => (
              <Grid item xs={12} md={6} key={detail.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: `${colors.primary[600]}CC`,
                    border: `1px solid ${colors.primary[200]}30`,
                    boxShadow: `0 10px 28px ${colors.primary[900]}45`,
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 48,
                      height: 48,
                      background: detail.gradient,
                      color: colors.grey[100],
                      boxShadow: `0 6px 18px ${colors.primary[900]}40`,
                    }}
                  >
                    {detail.icon}
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        color: colors.grey[300],
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      {detail.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.grey[100],
                        fontSize: "1.15rem",
                        fontWeight: 600,
                        mt: 0.3,
                      }}
                    >
                      {detail.value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      {/* Hastalar Tablosu */}
      <Card
        sx={{
          ...dataGridCardStyles,
          width: "95%",
          mx: "auto",
          mb: 5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            py: 3,
            borderBottom: `1px solid ${colors.grey[800]}30`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                boxShadow: `0 6px 18px ${colors.blueAccent[500]}40`,
              }}
            >
              <PeopleAltOutlinedIcon sx={{ color: colors.grey[100] }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.3 }}
              >
                {t('patientsAssociatedWithThisFile')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], mt: 0.5, opacity: 0.85 }}
              >
                {formInfo.type === "for me" 
                  ? t('select1PatientToFill')
                  : `${t('selectPatientsToSend')} ${selectedPatients.length > 0 ? `(${selectedPatients.length} ${t('selected')})` : ""}`
                }
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${patients.length} ${patients.length === 1 ? t('patient') : t('patients')}`}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              color: colors.blueAccent[100],
              fontWeight: 600,
              borderRadius: 2,
              px: 1.5,
            }}
          />
        </Box>
        <Box sx={dataGridWrapperStyles}>
          <DataGrid
            checkboxSelection
            rows={patients}
            autoHeight
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            columns={columns}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{ toolbar: { placeholder: t('searchPatients') } }}
            onRowSelectionModelChange={(selectedIds) => {
              const selectedData = patients.filter((row) =>
                selectedIds.includes(row.id)
              );
              setSelectedPatients(selectedData);
            }}
          />
        </Box>
      </Card>

        <Box sx={{ width: "90%", ml: 6 }}>
        <Box
          sx={{
            mb: 4,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 14px 32px ${colors.primary[900]}40`,
            border: `1px solid ${colors.primary[200]}30`,
            backgroundColor: colors.primary[500],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2.5, md: 3.5 },
              py: 2.5,
              background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  backgroundColor: colors.primary[500],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.grey[100],
                  boxShadow: `0 10px 26px ${colors.primary[900]}55`,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    fontSize: "1.1rem",
                  }}
                >
                  {t('formDetails')}
                </Typography>
                <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                  {t('reviewTitleAndDescription')}
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Chip
                label={`${t('fileLabel')} ${formInfo?.File?.name || "—"}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1,
                }}
              />
              <Chip
                label={`${t('typeLabel')} ${formInfo?.type === "for me" ? t('forDoctor') : t('forPatient')}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1,
                }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: colors.grey[300],
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t('formTitle')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('formTitlePlaceholder')}
                value={formInfo?.name || ""}
                InputProps={{
                  readOnly: true,
                  sx: {
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    fontSize: "1.7rem",
                    fontWeight: 700,
                    color: colors.grey[100],
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}44`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.greenAccent[400],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.greenAccent[500],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    color: colors.grey[400],
                    "&.Mui-focused": {
                      color: colors.greenAccent[300],
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
              >
                {(formInfo?.name?.length ?? 0)} {t('characters')}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: colors.grey[300],
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t('formDescription')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                placeholder={t('formDescriptionPlaceholder')}
                value={formInfo?.description || ""}
                InputProps={{
                  readOnly: true,
                  sx: {
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    color: colors.grey[200],
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}33`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.blueAccent[300],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.blueAccent[200],
                      boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    color: colors.grey[400],
                    "&.Mui-focused": {
                      color: colors.blueAccent[300],
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
              >
                {(formInfo?.description?.length ?? 0)} {t('characters')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          {formInfo?.questions?.map((field, index) => {
            const meta = questionMeta[field.type] || questionMeta.default;
            const excludedFromScoring = field.hasImportance === false;
            const questionSubtitle = excludedFromScoring
              ? t("thisQuestionWillNotBeIncluded")
              : meta.subtitle;

            return (
              <Box key={index} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: `0 12px 30px ${colors.primary[900]}40`,
                    border: `1px solid ${colors.primary[200]}30`,
                    backgroundColor: colors.primary[500],
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: { xs: 2.5, md: 3.5 },
                      py: 2.5,
                      background: meta.gradient,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 3,
                          backgroundColor: colors.primary[500],
                          color: colors.grey[100],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 8px 20px ${colors.primary[900]}55`,
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: colors.grey[100],
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('questionNumber', { index: index + 1 })} {meta.title}
                        </Typography>
                        {questionSubtitle &&
                          (excludedFromScoring ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                mt: 0.75,
                                px: 1.1,
                                py: 0.45,
                                borderRadius: 1.5,
                                border: `1px solid ${colors.redAccent[400]}`,
                                backgroundColor: "rgba(0, 0, 0, 0.38)",
                                boxShadow: `0 1px 6px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255,255,255,0.08)`,
                              }}
                            >
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  color: "#ffffff",
                                  fontWeight: 700,
                                  letterSpacing: 0.15,
                                  lineHeight: 1.35,
                                  fontSize: "0.8125rem",
                                  textShadow: "0 1px 1px rgba(0,0,0,0.45)",
                                }}
                              >
                                {questionSubtitle}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: colors.grey[200], opacity: 0.85 }}
                            >
                              {questionSubtitle}
                            </Typography>
                          ))}
                      </Box>
                    </Box>
                    <Chip
                      label={meta.chipLabel}
                      sx={{
                        backgroundColor: `${colors.primary[500]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      px: { xs: 2.5, md: 3.5 },
                      py: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: colors.grey[300],
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        {t('questionText')}
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={field.question || ""}
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          maxWidth: 760,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            backgroundColor: colors.primary[500],
                            fontSize: "1.05rem",
                            fontWeight: 600,
                            color: colors.grey[100],
                            '& fieldset': {
                              borderColor: `${colors.primary[200]}44`,
                            },
                            '&:hover fieldset': {
                              borderColor: colors.greenAccent[400],
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.greenAccent[500],
                              boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                            },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "right",
                          mt: 0.5,
                          color: colors.grey[400],
                        }}
                      >
                        {(field.question?.length ?? 0)} {t('characters')}
                      </Typography>
                    </Box>

                    {field.type === "text" && (
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          backgroundColor: `${colors.primary[600]}99`,
                          border: `1px solid ${colors.primary[200]}22`,
                          maxWidth: 760,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: colors.grey[300],
                            mb: 1,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('patientPreview')}
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder={t('patientResponseWillAppearHere')}
                          disabled
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              backgroundColor: colors.primary[500],
                              color: colors.grey[400],
                              '& fieldset': {
                                borderColor: `${colors.primary[200]}33`,
                              },
                            },
                          }}
                        />
                      </Box>
                    )}

                    {field.type === "multiple_choice" && (() => {
                      /* createForm ile aynı: hasImportance === false → puan hesabına dahil değil, importance gösterilmez */
                      const showOptionImportance = field.hasImportance !== false;
                      return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: colors.grey[300],
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          {t('options')}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {field.options.map((option, optionIdx) => (
                            <Box
                              key={optionIdx}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "auto 1fr",
                                  md: showOptionImportance ? "auto 1fr 180px" : "auto 1fr",
                                },
                                gap: { xs: 1.5, md: 2 },
                                alignItems: "center",
                                backgroundColor: `${colors.primary[600]}AA`,
                                borderRadius: 3,
                                border: `1px solid ${colors.primary[200]}33`,
                                boxShadow: `0 8px 18px ${colors.primary[900]}25`,
                                px: { xs: 1.5, md: 2.5 },
                                py: { xs: 1.2, md: 1.6 },
                              }}
                            >
                              <Radio
                                disabled
                                sx={{
                                  color: colors.greenAccent[500],
                                  '&.Mui-disabled': {
                                    color: colors.greenAccent[600],
                                  },
                                }}
                              />
                              <MuiTextField
                                value={option.option}
                                label={`${t('option')} ${optionIdx + 1}`}
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  readOnly: true,
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: colors.primary[500],
                                    '& fieldset': {
                                      borderColor: `${colors.primary[200]}33`,
                                    },
                                    '&:hover fieldset': {
                                      borderColor: colors.greenAccent[400],
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: colors.greenAccent[500],
                                      boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: colors.grey[400],
                                    '&.Mui-focused': {
                                      color: colors.greenAccent[300],
                                    },
                                  },
                                }}
                              />
                              {showOptionImportance && (
                              <MuiTextField
                                type="number"
                                value={option.option_level ?? ""}
                                label={t('importance')}
                                variant="outlined"
                                InputProps={{
                                  readOnly: true,
                                }}
                                sx={{
                                  width: { xs: "100%", md: "160px" },
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: colors.primary[500],
                                    '& fieldset': {
                                      borderColor: `${colors.primary[200]}33`,
                                    },
                                    '&:hover fieldset': {
                                      borderColor: colors.blueAccent[400],
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: colors.blueAccent[300],
                                      boxShadow: `0 0 0 3px ${colors.blueAccent[300]}33`,
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    textAlign: "center",
                                    color: colors.grey[100],
                                    fontWeight: 600,
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: colors.grey[400],
                                    '&.Mui-focused': {
                                      color: colors.blueAccent[300],
                                    },
                                  },
                                }}
                              />
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      );
                    })()}

                    {field.type === "table" && (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 3,
                          }}
                        >
                          <TableChartIcon
                            sx={{
                              fontSize: 28,
                              color: colors.greenAccent[500],
                            }}
                          />
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: "bold",
                              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            {t('tableQuestion')}
                          </Typography>
                          <Box
                            sx={{
                              ml: "auto",
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                backgroundColor: colors.blueAccent[700],
                                border: `1px solid ${colors.blueAccent[500]}`,
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                📝 {t('rowsLabel')} {field.tableRows}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                backgroundColor: colors.greenAccent[700],
                                border: `1px solid ${colors.greenAccent[500]}`,
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                📊 {t('columnsLabel')} {field.tableCols}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            overflowX: "auto",
                            borderRadius: 2,
                            border: `3px solid ${colors.greenAccent[700]}`,
                            boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
                            background: colors.primary[500],
                          }}
                        >
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                                    fontWeight: "bold",
                                    border: `2px solid ${colors.blueAccent[600]}`,
                                    color: colors.grey[100],
                                    fontSize: "1rem",
                                    textAlign: "center",
                                    minWidth: 180,
                                  }}
                                >
                                  ❓ {t('questionsLabel')}
                                </TableCell>
                                {field.tableHeaders.map((header, headerIdx) => (
                                  <TableCell
                                    key={headerIdx}
                                    sx={{
                                      background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                                      fontWeight: "bold",
                                      border: `2px solid ${colors.greenAccent[600]}`,
                                      minWidth: 140,
                                      padding: 2,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.95rem",
                                          fontWeight: "bold",
                                          color: colors.grey[100],
                                        }}
                                      >
                                        {header}
                                      </Typography>
                                      {field.hasImportance !== false && (
                                      <Box
                                        sx={{
                                          px: 2,
                                          py: 0.5,
                                          borderRadius: 2,
                                          backgroundColor: colors.primary[600],
                                          border: `1px solid ${colors.greenAccent[500]}`,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: "bold",
                                            color: colors.grey[100],
                                          }}
                                        >
                                          💯{" "}
                                          {t("scoreLabel", {
                                            score:
                                              field.tableHeaderImportance?.[
                                                headerIdx
                                              ] ?? 0,
                                          })}
                                        </Typography>
                                      </Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {field.tableQuestions.map((question, questionIdx) => (
                                <TableRow
                                  key={questionIdx}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: colors.primary[600],
                                    },
                                    transition: "background-color 0.2s",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      backgroundColor: colors.blueAccent[900],
                                      border: `1px solid ${colors.blueAccent[700]}`,
                                      minWidth: 180,
                                      color: colors.grey[100],
                                      fontWeight: "500",
                                    }}
                                  >
                                    {question}
                                  </TableCell>
                                  {field.tableHeaders.map((header, headerIdx) => (
                                    <TableCell
                                      key={headerIdx}
                                      sx={{
                                        border: `1px solid ${colors.greenAccent[700]}`,
                                        textAlign: "center",
                                        backgroundColor: colors.primary[600],
                                        "&:hover": {
                                          backgroundColor: colors.primary[500],
                                        },
                                        transition: "background-color 0.2s",
                                      }}
                                    >
                                      <Radio
                                        disabled
                                        size="small"
                                        sx={{
                                          color: colors.greenAccent[600],
                                          "&.Mui-disabled": {
                                            color: colors.greenAccent[700],
                                          },
                                        }}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: colors.primary[600],
                            border: `1px solid ${colors.blueAccent[700]}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.blueAccent[400],
                              fontStyle: "italic",
                            }}
                          >
                            ℹ️ {t('tableQuestionInfo')}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {field.type === "rating_scale" && (
                      <Box sx={{ mt: 2, maxWidth: 720 }}>
                        <RatingScaleSlider
                          value={5}
                          onChange={() => {}}
                          minLabel={field.minLabel || "0"}
                          maxLabel={field.maxLabel || "10"}
                          positiveEnd={field.positiveEnd === "max" ? "max" : "min"}
                          disabled
                          colors={colors}
                        />
                      </Box>
                    )}

                    {field.type === "image" && (
                      <Box>
                        {(() => {
                          const imageMode = field.imageAnswerMode || "select";
                          const hasImage = Boolean(field.imageUrl);
                          const hasAreas = Array.isArray(field.areas) && field.areas.length > 0;
                          return (
                            <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 3,
                          }}
                        >
                          <ImageIcon
                            sx={{
                              fontSize: 28,
                              color: colors.greenAccent[500],
                            }}
                          />
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: "bold",
                              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            {t('imageQuestionICRS')}
                          </Typography>
                          <Box
                            sx={{
                              ml: "auto",
                              px: 2,
                              py: 0.5,
                              borderRadius: 2,
                              backgroundColor: colors.greenAccent[700],
                              border: `1px solid ${colors.greenAccent[500]}`,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              {imageMode === "draw"
                                ? "✏️ Mode: Draw"
                                : `🗺️ ${t('areasLabel')} ${field.areas?.length || 0}`}
                            </Typography>
                          </Box>
                        </Box>

                        {hasImage && imageMode === "draw" ? (
                          <Box>
                            <Box
                              sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                border: `2px solid ${colors.blueAccent[700]}`,
                                backgroundColor: colors.primary[600],
                                p: 1,
                              }}
                            >
                              <img
                                src={field.imageUrl}
                                alt="question preview"
                                style={{ width: "100%", maxHeight: 500, objectFit: "contain", display: "block" }}
                              />
                            </Box>
                          </Box>
                        ) : hasImage && hasAreas ? (
                          <Box>
                            <CartilageMap
                              imageUrl={field.imageUrl}
                              areas={field.areas}
                              disabled
                              showLabels
                            />

                            <Box
                              sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: colors.primary[600],
                                border: `2px solid ${colors.blueAccent[700]}`,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 2,
                                  color: colors.greenAccent[400],
                                  fontWeight: "bold",
                                }}
                              >
                                📋 {t('definedAreasLabel')} ({field.areas.length})
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {field.areas.map((area, areaIdx) => (
                                  <Box
                                    key={area.id || areaIdx}
                                    sx={{
                                      p: 2,
                                      backgroundColor: colors.primary[500],
                                      borderRadius: 2,
                                      border: `1px solid ${colors.blueAccent[800]}`,
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: "bold",
                                          color: colors.grey[100],
                                          mb: 0.5,
                                        }}
                                      >
                                        {areaIdx + 1}. {area.name}
                                      </Typography>
                                      {area.description && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: colors.grey[400],
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {area.description}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box
                                      sx={{
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 2,
                                        backgroundColor: colors.blueAccent[700],
                                        border: `1px solid ${colors.blueAccent[500]}`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: "bold",
                                          color: colors.grey[100],
                                        }}
                                      >
                                        {t('valueLabel')} {area.value}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              textAlign: "center",
                              backgroundColor: colors.primary[600],
                              border: `2px dashed ${colors.grey[600]}`,
                              borderRadius: 2,
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 48, color: colors.grey[500], mb: 1 }} />
                            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                              {imageMode === "draw" ? t('noImageOrAreas') : t('noImageOrAreas')}
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: colors.primary[600],
                            border: `1px solid ${colors.blueAccent[700]}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.blueAccent[400],
                              fontStyle: "italic",
                            }}
                          >
                            ℹ️ {t('imageQuestionInfo')}
                          </Typography>
                        </Box>
                            </>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: { xs: 1.5, md: 2.5 },
              mt: 2,
            }}
          >
            <Button
              size="large"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 22 }} />}
              sx={{
                background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                color: colors.grey[100],
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                py: 1.3,
                textTransform: "none",
                letterSpacing: 0.4,
                boxShadow: `0 12px 24px ${colors.blueAccent[900]}40`,
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-3px) scale(1.02)",
                  boxShadow: `0 16px 32px ${colors.blueAccent[900]}55`,
                  background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                },
              }}
              onClick={handleEditForm}
            >
              {t('editFormButton')}
            </Button>
            <Button
              size="large"
              startIcon={<ContentCopyIcon />}
              sx={{
                background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                color: colors.grey[100],
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                py: 1.3,
                textTransform: "none",
                letterSpacing: 0.4,
                boxShadow: `0 12px 28px ${colors.greenAccent[700]}45`,
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-3px) scale(1.02)",
                  boxShadow: `0 18px 32px ${colors.greenAccent[700]}60`,
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                },
              }}
              onClick={handleOpenCopyDialog}
            >
              {t('copyButton')}
            </Button>
            {formInfo.type === "for me" ? (
              <Button
                size="large"
                startIcon={<EditOutlinedIcon sx={{ fontSize: 22 }} />}
                sx={{
                  background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                  color: colors.grey[100],
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 3,
                  py: 1.3,
                  textTransform: "none",
                  letterSpacing: 0.4,
                  boxShadow: `0 12px 26px ${colors.greenAccent[700]}40`,
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-3px) scale(1.02)",
                    boxShadow: `0 18px 34px ${colors.greenAccent[700]}55`,
                    background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                  },
                }}
                onClick={handleFill}
              >
                {t('fillInForm')}
              </Button>
            ) : (
              <Button
                onClick={handleSend}
                disabled={sendEmailLoading}
                size="large"
                startIcon={
                  sendEmailLoading ? (
                    <Box component="span" sx={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <Box
                        component="span"
                        sx={{
                          width: 18,
                          height: 18,
                          border: `2px solid ${colors.grey[100]}`,
                          borderBottomColor: "transparent",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                          "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                        }}
                      />
                    </Box>
                  ) : (
                    <EmailOutlinedIcon sx={{ fontSize: 22 }} />
                  )
                }
                sx={{
                  background: sendEmailLoading
                    ? colors.grey[700]
                    : `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                  color: colors.grey[100],
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 3,
                  py: 1.3,
                  textTransform: "none",
                  letterSpacing: 0.4,
                  boxShadow: `0 12px 26px ${colors.greenAccent[700]}40`,
                  transition: "all 0.25s ease",
                  "&:hover": sendEmailLoading
                    ? {}
                    : {
                        transform: "translateY(-3px) scale(1.02)",
                        boxShadow: `0 18px 34px ${colors.greenAccent[700]}55`,
                        background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                      },
                  "&.Mui-disabled": {
                    color: colors.grey[300],
                    background: colors.grey[700],
                  },
                }}
              >
                {sendEmailLoading ? t('sending') : t('sendByMail')} {!sendEmailLoading && selectedPatients.length > 0 && `(${selectedPatients.length})`}
              </Button>
            )}
          </Box>
          {showAlert && (
            <WarningSnackbar
              severity={alertSeverity}
              message={alertMessage}
              open={showAlert}
              autoHideDuration={3000}
              onClose={handleCloseSnackbar}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 22 }} />}
            sx={{
              color: colors.grey[100],
              background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[500]} 100%)`,
              fontWeight: 700,
              borderRadius: 3,
              px: 3,
              py: 1.3,
              textTransform: "none",
              letterSpacing: 0.4,
              boxShadow: `0 12px 28px ${colors.redAccent[900]}45`,
              transition: "all 0.25s ease",
              "&:hover": {
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: `0 18px 36px ${colors.redAccent[900]}60`,
                background: `linear-gradient(135deg, ${colors.redAccent[700]} 0%, ${colors.redAccent[500]} 100%)`,
              },
            }}
            onClick={() => {
              setOpenDeleteForm(true);
            }}
          >
            {t('deleteForm')}
          </Button>
          <ConfirmationDialog
            open={openDeleteForm}
            title={t('deleteFormTitle')}
            description={t('deleteFormDescription')}
            onClose={closeDeleteFormDialog}
            onConfirm={handleDeleteForm}
          />
        </Box>

        {/* Copy Form Dialog */}
        <Dialog
          open={openCopyDialog}
          onClose={handleCloseCopyDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: colors.primary[400],
              borderRadius: 3,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: `2px solid ${colors.greenAccent[400]}`,
            pb: 2
          }}>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2
            }}>
              <ContentCopyIcon sx={{ 
                fontSize: 28,
                color: colors.greenAccent[500]
              }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: "bold",
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}
              >
                {t('copyFormDialogTitle')}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Form Name Input */}
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5,
                    color: colors.blueAccent[400],
                    fontWeight: "bold",
                    mt:2
                  }}
                >
                  📝 {t('newFormName')}
                </Typography>
                <TextField
                  fullWidth
                  value={copyFormName}
                  onChange={(e) => setCopyFormName(e.target.value)}
                  placeholder={t('enterFormName')}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: colors.greenAccent[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.greenAccent[300],
                      }
                    }
                  }}
                />
              </Box>

              {/* File Selection */}
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5,
                    color: colors.greenAccent[400],
                    fontWeight: "bold"
                  }}
                >
                  📁 {t('selectTargetFile')}
                </Typography>
                <Select
                  fullWidth
                  value={selectedFileId}
                  onChange={(e) => setSelectedFileId(e.target.value)}
                  displayEmpty
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.primary[200],
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.greenAccent[400],
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.greenAccent[300],
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>{t('selectAFile')}</em>
                  </MenuItem>
                  {allFiles.map((file) => (
                    <MenuItem 
                      key={file.id} 
                      value={file.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: colors.greenAccent[700],
                          color: "white"
                        }
                      }}
                    >
                      {file.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Preview Info */}
              <Box sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: colors.primary[600],
                border: `1px solid ${colors.blueAccent[700]}`
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.blueAccent[400],
                    fontStyle: "italic"
                  }}
                >
                  {t('formCopyInfo')}
                </Typography>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ 
            p: 3, 
            borderTop: `2px solid ${colors.primary[300]}`,
            gap: 2
          }}>
            <Button 
              onClick={handleCloseCopyDialog}
              variant="outlined"
              sx={{
                borderColor: colors.redAccent[500],
                color: colors.redAccent[400],
                fontWeight: "bold",
                px: 3,
                "&:hover": {
                  borderColor: colors.redAccent[400],
                  backgroundColor: colors.redAccent[900],
                  transform: "scale(1.05)"
                },
                transition: "all 0.2s"
              }}
            >
              ✖ {t('cancel')}
            </Button>
            <Button 
              onClick={handleCopyForm}
              variant="contained"
              sx={{ 
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                color: "white",
                fontWeight: "bold",
                px: 4,
                boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[700]} 100%)`,
                  transform: "scale(1.05)",
                  boxShadow: `0 6px 16px rgba(0, 0, 0, 0.4)`
                },
                transition: "all 0.2s"
              }}
            >
              ✓ {t('copyFormButton')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
